/**
 * PerfBench — a deterministic, login-free harness page for measuring the chat
 * frontend's long-conversation render cost.
 *
 * It mounts the REAL <MessageList/> inside a real PlaygroundProvider, but with a
 * synthetic, controllable context: a generated N-message history, a settable
 * composer value, and a simulated stream. No Auth0, no backend, no network — so
 * the numbers isolate pure frontend render cost and are reproducible run to run.
 *
 * The route is registered only when the perf probe is enabled (build flag /
 * ?perfProbe=1), so a normal production build never exposes it.
 *
 * Drivers exposed to the Playwright harness on window.__perfBench__:
 *   setSize(n)             reset history to n synthetic messages
 *   setCurrentMessage(s)   set the composer value (reproduces the typing cascade)
 *   typeString(s)          type s char-by-char, one char per animation frame
 *   stream(s)              stream s into a new assistant message, one char/frame
 *   reset()                clear composer + streaming state
 *   state()                read current sizes
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import remarkMath from 'remark-math';
import rehypeKatex from 'rehype-katex';
import MessageList from '../components/playground/MessageList';
import { PlaygroundProvider, PlaygroundContextValue } from '../shared/context/PlaygroundContext';
import { ProfiledRegion } from '../utils/perfProbe';
import { preprocessingLatex } from '../components/playground/utils';

type BenchMessage = { role: 'user' | 'assistant'; content: string };

// ---- Deterministic synthetic content (no Math.random → reproducible) --------

const WORDS = [
  'lorem', 'ipsum', 'dolor', 'sit', 'amet', 'consectetur', 'adipiscing', 'elit',
  'sed', 'eiusmod', 'tempor', 'incididunt', 'labore', 'magna', 'aliqua', 'enim',
  'minim', 'veniam', 'quis', 'nostrud', 'exercitation', 'ullamco', 'laboris',
  'aliquip', 'commodo', 'consequat', 'aute', 'irure', 'reprehenderit', 'voluptate',
];

function sentence(seed: number, words: number): string {
  const parts: string[] = [];
  for (let i = 0; i < words; i++) {
    parts.push(WORDS[(seed * 7 + i * 13) % WORDS.length]);
  }
  const s = parts.join(' ');
  return s.charAt(0).toUpperCase() + s.slice(1) + '.';
}

function paragraph(seed: number, sentences: number): string {
  const out: string[] = [];
  for (let i = 0; i < sentences; i++) out.push(sentence(seed + i, 6 + ((seed + i) % 6)));
  return out.join(' ');
}

function genAssistant(i: number): string {
  const parts: string[] = [`## Resposta ${i}`, paragraph(i, 3)];
  if (i % 3 === 0) {
    parts.push('```python\n# exemplo ' + i + '\nfor x in range(' + (i % 9 + 2) + '):\n    print(x * ' + i + ')\n```');
  }
  if (i % 4 === 0) {
    parts.push('| Item | Valor |\n|------|-------|\n| a' + i + ' | ' + i + ' |\n| b' + i + ' | ' + i * 2 + ' |');
  }
  if (i % 5 === 0) {
    parts.push('A relação é $E = mc^2$ e a soma $\\sum_{k=1}^{' + (i % 7 + 1) + '} k = \\frac{n(n+1)}{2}$.');
  }
  parts.push('- item **um** ' + i + '\n- item _dois_\n- veja [a referência](https://example.com/r/' + i + ')');
  return parts.join('\n\n');
}

function genUser(i: number): string {
  return `**Pergunta ${i}.** ` + paragraph(i + 100, 1);
}

function makeMessages(n: number): BenchMessage[] {
  const out: BenchMessage[] = [];
  for (let i = 0; i < n; i++) {
    out.push(i % 2 === 0 ? { role: 'user', content: genUser(i) } : { role: 'assistant', content: genAssistant(i) });
  }
  return out;
}

// ---- Bench markdown renderer (real plugin pipeline, counted) -----------------

const benchFormatAssistantMessage = (text: string): React.ReactNode => (
  <ProfiledRegion id="assistantMarkdown">
    <ReactMarkdown
      remarkPlugins={[remarkGfm, [remarkMath, { singleDollarTextMath: true }]]}
      rehypePlugins={[rehypeKatex]}
    >
      {preprocessingLatex(text)}
    </ReactMarkdown>
  </ProfiledRegion>
);

const noop = (): void => undefined;

// Safe defaults for every context value MessageList reads. Overridable fields
// (history/composer/streaming) are spread in by the component below.
//
// CRITICAL: this is a stable module-level singleton, NOT a per-render factory.
// If it were rebuilt each render, its empty arrays/objects (currentToolCalls,
// extractingFiles, ...) would get fresh references every keystroke and the
// probe would wrongly attribute MessageList's re-renders to THEM instead of
// reporting them as wasted. Stable refs here make the wasted-render signal real.
const BASE_CONTEXT: Partial<PlaygroundContextValue> = {
    isMobile: false,
    loadingMessage: '',
    extractingFiles: [],
    chatErrorMessage: '',
    allowErrorRetry: false,
    showPlanUpgradeOption: false,
    isEditing: false,
    editingMessageId: null,
    messageBeingEdited: '',
    copiedIndex: null,
    planStatus: null,
    planTitle: null,
    freeQuota: null,
    quotaBadgeIndex: null,
    filePreviews: {},
    fileTextPreviews: {},
    numPages: null,
    fileDownloadProgress: {},
    loadingOcrModal: false,
    activeChatID: null as unknown as PlaygroundContextValue['activeChatID'],
    handleEditMessage: noop,
    handleSaveEditedMessage: noop,
    handleCancelEdit: noop,
    handleEditMessageInputChange: noop,
    handleCopyToClipboard: noop,
    handleLikeMessage: noop,
    handleDislikeClick: noop,
    handleRegenerateMessage: noop,
    handleRetryMessage: noop,
    handleContinueGeneration: noop,
    handleOpenPreview: noop,
    handleOpenOcrModal: noop,
    hasOcrContentForMessage: () => false,
    setOpenSubscriptionModal: noop,
    getVisibleReferences: () => [],
    getCitedReferencesWithHistory: () => ({ references: [], hasHistoryRefs: false }),
    renderQuotaInfoTrigger: () => null,
    deepResearchPhase: '',
    deepResearchProgress: 0,
    deepResearchPlan: [],
    deepResearchSteps: {},
    deepResearchTokens: { input: 0, output: 0 },
    panoramaPhase: '',
    panoramaProgress: 0,
    panoramaNSearches: 0,
    panoramaNDocs: 0,
    panoramaNTheses: 0,
    panoramaInputTokens: 0,
    panoramaOutputTokens: 0,
    debugMode: false,
    currentToolCalls: [],
    formatAssistantMessage: (m: string | unknown) => benchFormatAssistantMessage(typeof m === 'string' ? m : String(m ?? '')),
};

declare global {
  interface Window {
    __perfBench__?: {
      setSize: (n: number) => void;
      setCurrentMessage: (s: string) => void;
      typeString: (s: string) => Promise<void>;
      stream: (s: string) => Promise<void>;
      reset: () => void;
      state: () => { size: number; currentMessage: number; streaming: number };
      ready: true;
    };
  }
}

const PerfBench: React.FC = () => {
  const [messages, setMessages] = useState<BenchMessage[]>(() => makeMessages(200));
  const [currentMessage, setCurrentMessage] = useState('');
  const [messageBeingGenerated, setMessageBeingGenerated] = useState('');
  const [loadingGeneration, setLoadingGeneration] = useState(false);
  const endRef = useRef<HTMLDivElement>(null);

  // Mirror live state into a ref so window.__perfBench__.state() is never stale.
  const stateRef = useRef({ size: 0, currentMessage: 0, streaming: 0 });
  stateRef.current = { size: messages.length, currentMessage: currentMessage.length, streaming: messageBeingGenerated.length };

  useEffect(() => {
    // setTimeout, NOT requestAnimationFrame: a measurement harness may run in a
    // hidden/background tab where rAF is paused, which would stall the drivers.
    // The yield between steps is also load-bearing — it lets React commit each
    // update as a SEPARATE render (synchronous setState calls batch into one),
    // so render counts match real per-keystroke / per-token behavior.
    const schedule = (cb: () => void) => setTimeout(cb, 0);

    window.__perfBench__ = {
      setSize: (n: number) => setMessages(makeMessages(n)),
      setCurrentMessage: (s: string) => setCurrentMessage(s),
      reset: () => {
        setCurrentMessage('');
        setMessageBeingGenerated('');
        setLoadingGeneration(false);
      },
      typeString: (text: string) =>
        new Promise<void>((resolve) => {
          let i = 0;
          const step = () => {
            setCurrentMessage(text.slice(0, i));
            if (i >= text.length) return resolve();
            i += 1;
            schedule(step);
          };
          schedule(step);
        }),
      // Stream into a fresh trailing assistant message — this is the production
      // path: the last message's content grows each token while
      // messageBeingGenerated stays truthy, so MessageList re-renders the whole
      // list and re-parses the growing message on every step.
      stream: (text: string) =>
        new Promise<void>((resolve) => {
          setMessages((prev) => [...prev, { role: 'assistant', content: '' }]);
          setLoadingGeneration(true);
          setMessageBeingGenerated(' ');
          let k = 0;
          const step = () => {
            const partial = text.slice(0, k);
            setMessages((prev) => {
              const copy = prev.slice();
              copy[copy.length - 1] = { role: 'assistant', content: partial };
              return copy;
            });
            setMessageBeingGenerated(partial || ' ');
            if (k >= text.length) {
              setMessageBeingGenerated('');
              setLoadingGeneration(false);
              return resolve();
            }
            k += 1;
            schedule(step);
          };
          schedule(step);
        }),
      state: () => ({ ...stateRef.current }),
      ready: true,
    };
    // eslint-disable-next-line no-console
    console.info('[perfBench] ready — window.__perfBench__ available');
    return () => {
      delete window.__perfBench__;
    };
  }, []);

  const value = useMemo<PlaygroundContextValue>(
    () =>
      ({
        ...BASE_CONTEXT,
        messageHistory: messages as unknown as PlaygroundContextValue['messageHistory'],
        currentMessage,
        messageBeingGenerated,
        loadingGeneration,
        endOfChatMessageMarkerRef: endRef,
      }) as unknown as PlaygroundContextValue,
    // currentMessage is intentionally a dep: changing it must rebuild the value
    // object so every consumer re-renders — faithfully reproducing the real
    // provider, which rebuilds its value on every Playground render.
    [messages, currentMessage, messageBeingGenerated, loadingGeneration],
  );

  return (
    <div
      data-perf-bench-scroll
      style={{ height: '100vh', overflowY: 'auto', padding: 16, maxWidth: 900, margin: '0 auto' }}
    >
      <PlaygroundProvider value={value}>
        <ProfiledRegion id="MessageList">
          <MessageList />
        </ProfiledRegion>
        <div ref={endRef} />
      </PlaygroundProvider>
    </div>
  );
};

export default PerfBench;
